// Apple "silent voicemail" guard — config + pure decision logic.
//
// A silent voicemail connects like a normal call but sends no far-side audio, and
// Twilio's AMD does not reliably catch it. The cockpit watches the live volume
// stream and, when a call gives dead air while the SDR was speaking, raises an
// informational "couldn't confirm a human" banner (it does NOT hang up — a live
// contact who listens quietly to the opener looks identical, so we never cut the
// call; the SDR uses the existing Drop Call button if it really is a voicemail).
//
// Dead air alone does not identify a voicemail — the window opens at accept, after
// the contact's "Hello?" has passed. The confirmed gate keeps the signal honest: the
// prompt only fires while the core has not landed a real AMD `human` verdict, which
// is the population silent voicemails live in.
//
// Knobs are build-time env (VITE_VOICEMAIL_*). Parsing and the decision are pure so
// they test without timers or a Twilio Call; the caller owns the timer and level
// tracking and calls isVoicemail() once at window expiry.

export interface VoicemailConfig {
  windowMs: number; // dead-air window measured from call connect (accept)
  remoteMax: number; // remote-silence ceiling (0–1); output above this = far side has audio
  localMin: number; // local-speech floor (0–1); our input above this = we spoke
}

// Defaults — the shipping behaviour when the VITE_VOICEMAIL_* env is unset.
export const VOICEMAIL_DEFAULTS: VoicemailConfig = {
  // Must outlast the AMD verdict, or the prompt fires before the confirmation that
  // would have stopped it. From call-log timestamps over thousands of calls: ~99% of
  // verdicts land by 7s, slowest ~8s, with a single 11s outlier this window
  // deliberately does not cover. Twilio's own ceiling is MachineDetectionTimeout
  // (amd_timeout_s, 30s); sizing to that would prompt on every voicemail far too
  // late, so the tail is an accepted trade. The prompt is non-destructive either way.
  windowMs: 10_000,
  remoteMax: 0.05, // 5%
  localMin: 0.2, // 20%
};

// Parse the build-time env (import.meta.env) into a config, falling back to the
// default for any unset/garbage value. Levels outside 0–1 and non-positive windows
// are ignored (→ default).
export function readVoicemailConfig(env: Record<string, unknown>): VoicemailConfig {
  return {
    windowMs: parsePositive(env.VITE_VOICEMAIL_WINDOW_MS, VOICEMAIL_DEFAULTS.windowMs),
    remoteMax: parseUnit(env.VITE_VOICEMAIL_REMOTE_MAX, VOICEMAIL_DEFAULTS.remoteMax),
    localMin: parseUnit(env.VITE_VOICEMAIL_LOCAL_MIN, VOICEMAIL_DEFAULTS.localMin),
  };
}

// What the detector observed across the window, evaluated at expiry.
export interface VoicemailObservation {
  remoteHeard: boolean; // far-side output crossed remoteMax at some point in the window
  localSpoke: boolean; // our input crossed localMin at some point in the window
  humanConfirmed: boolean; // a real AMD `human` verdict landed, not merely a card
}

// Raise the prompt iff we spoke into dead air and nobody confirmed a human. Defensive
// on remoteHeard: the caller also cancels the timer as soon as remote audio is heard.
export function isVoicemail(obs: VoicemailObservation): boolean {
  if (obs.humanConfirmed) return false;
  return obs.localSpoke && !obs.remoteHeard;
}

// The two level thresholds, as pure predicates the hook shares across its volume
// handler. Extracted so the "far side spoke" / "we spoke" decisions — which arm the
// guard, drop the "Connecting…" label, and dismiss the banner — test without a
// Twilio Call, like isVoicemail.

// The far side is audible: output crossed remoteMax (a human, or a real voicemail
// greeting). Once true it cancels the guard timer and auto-dismisses a raised prompt.
// Pick<>, not the full config: the silence gate (cockpit/silence-gate.ts) shares
// these predicates with ITS thresholds so "audible" can never mean two things.
export function remoteAudible(output: number, cfg: Pick<VoicemailConfig, 'remoteMax'>): boolean {
  return output > cfg.remoteMax;
}

// We are audible: our input crossed localMin.
export function localAudible(input: number, cfg: Pick<VoicemailConfig, 'localMin'>): boolean {
  return input > cfg.localMin;
}

// The config in effect for this build, read once from the bundle's env.
export const voicemailConfig: VoicemailConfig = readVoicemailConfig(
  import.meta.env as unknown as Record<string, unknown>,
);

// -- env parsing helpers ------------------------------------------------------

function parsePositive(raw: unknown, fallback: number): number {
  const n = typeof raw === 'string' ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

// A 0–1 level threshold; anything outside that (or non-numeric) falls back.
function parseUnit(raw: unknown, fallback: number): number {
  const n = typeof raw === 'string' ? Number(raw) : NaN;
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback;
}
